'use client';

import { Rocket, Sparkles } from 'lucide-react';
import { Trans, useTranslation } from 'react-i18next';

import { QUICK_START_REQUIRED_POINTS } from '@/components/setup/quick-start/quickStartHub.utils';
import { Card } from '@/components/ui/card';
import type { QuickStartProgress } from '@/lib/api/setup.types';
import { cn } from '@/lib/utils';

type QuickStartScorePanelProps = {
  progress: QuickStartProgress;
};

const ScoreBar = ({ label, percent }: { label: string; percent: number }) => {
  const clamped = Math.max(0, Math.min(100, Math.round(percent)));
  return (
    <div>
      <div className="flex items-center gap-2">
        <span className="bg-primary size-2 shrink-0 rounded-full" />
        <span className="text-foreground text-sm font-medium">{label}</span>
      </div>
      {/* Track via inline color-mix, NOT a Tailwind opacity modifier. Theme colors
          here are bare var(--x) with no alpha-value placeholder, so a slash-opacity
          background compiles to an invalid value and the empty track renders invisible. */}
      <div
        className="mt-2 h-1.5 w-full overflow-hidden rounded-full"
        style={{ backgroundColor: 'color-mix(in srgb, var(--muted-foreground) 28%, transparent)' }}
      >
        <div
          className="bg-primary h-full rounded-full transition-[width] duration-500 ease-out"
          style={{ width: `${clamped}%` }}
        />
      </div>
    </div>
  );
};

/** Weighted store score (0–100): required steps are worth 70 pts, recommended 30. */
export function QuickStartScorePanel({ progress }: QuickStartScorePanelProps) {
  const { t } = useTranslation('setup');
  const requiredDone = progress.required.percent >= 100;

  return (
    <Card className="gap-0 py-0">
      <div className="p-6">
        <p className="text-muted-foreground text-xs font-semibold uppercase tracking-wider">
          {t('quickStart.scorePanel.eyebrow')}
        </p>
        <div className="mt-1 flex items-baseline gap-1">
          <span className="text-primary text-5xl font-bold tabular-nums tracking-tight">
            {progress.totalPercent}
          </span>
          <span className="text-muted-foreground text-lg font-medium">/100</span>
        </div>
        <p className="text-muted-foreground mt-3 text-sm leading-relaxed">
          {t('quickStart.scorePanel.caption')}
        </p>

        <div className="border-border mt-6 space-y-4 border-t pt-5">
          <ScoreBar label={t('quickStart.progress.required')} percent={progress.required.percent} />
          <ScoreBar
            label={t('quickStart.progress.recommended')}
            percent={progress.recommended.percent}
          />
        </div>

        <div
          className={cn(
            'mt-5 flex items-start gap-2 rounded-lg border px-3 py-2.5 text-[13px] leading-relaxed',
            requiredDone
              ? 'border-primary/30 bg-primary/5 text-foreground'
              : 'border-amber-200 bg-amber-50 text-amber-900 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-100',
          )}
        >
          {requiredDone ? (
            <Rocket className="text-primary mt-px size-4 shrink-0" aria-hidden />
          ) : (
            <Sparkles className="mt-px size-4 shrink-0 text-amber-500 dark:text-amber-400" aria-hidden />
          )}
          <span>
            {requiredDone ? (
              <Trans
                i18nKey="quickStart.scorePanel.unlocked"
                ns="setup"
                components={{ b: <span className="font-semibold" /> }}
              />
            ) : (
              <Trans
                i18nKey="quickStart.scorePanel.unlock"
                ns="setup"
                values={{ points: QUICK_START_REQUIRED_POINTS }}
                components={{ b: <span className="font-semibold" /> }}
              />
            )}
          </span>
        </div>
      </div>
    </Card>
  );
}
